import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import PageTransition from '@/components/ui/PageTransition';
import { Card, Button, Flex, Typography, Tag, Spin, Empty, theme } from 'antd';
import {
  ArrowLeftOutlined,
  ClockCircleOutlined,
  SettingOutlined,
  ClusterOutlined,
  ScheduleOutlined,
  PhoneOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { useGetServiceQuery } from '@/api/mcp-appointments';
import {
  ServiceGeneralSettings,
  ServiceAvailabilitySettings,
  ServiceResourcesSettings
} from '@/components/services/settings';
import { ServiceRolesDialog } from '@/components/service-roles';
import { DetailPageHeader } from '@/components/ui/DetailPageHeader';
import { SettingsTabsCard } from '@/components/resources/settings/SettingsTabsCard';
import { createManageRouteGuard } from '@/utils/manageRouteGuards';
import { TENANT_ROLES } from '@/constants/tenantRoles';
import { tabSearchParam } from '@/utils/searchParams';

const { Text } = Typography;

const MANAGE_TABS = ['general', 'availability', 'resources'] as const;
type ManageTab = (typeof MANAGE_TABS)[number];
const DEFAULT_MANAGE_TAB: ManageTab = 'general';

const LEGACY_TAB_MAP: Record<string, ManageTab> = {
  ai: 'general',
  'client-info': 'general',
  requirements: 'resources',
  'linked-resources': 'resources'
};

export const Route = createFileRoute('/manage/company-services/$serviceId/manage')({
  beforeLoad: createManageRouteGuard({ requiredTenantRoles: [TENANT_ROLES.OWNER] }),
  validateSearch: (search: Record<string, unknown>) => ({
    tab: tabSearchParam(search.tab, MANAGE_TABS, DEFAULT_MANAGE_TAB, LEGACY_TAB_MAP)
  }),
  component: ServiceManagePage
});

function ServiceManagePage() {
  const { t } = useTranslation('manage');
  const { serviceId } = Route.useParams();
  const navigate = Route.useNavigate();
  const { tab } = Route.useSearch();
  const { token } = theme.useToken();
  const [serviceRolesDialogOpen, setServiceRolesDialogOpen] = useState(false);

  const { data: service, isPending } = useGetServiceQuery(serviceId);

  if (isPending) {
    return (
      <PageTransition>
        <Flex
          vertical
          align="center"
          justify="center"
          gap="middle"
          style={{ minHeight: '60vh', textAlign: 'center' }}
        >
          <Spin size="large" />
          <Text type="secondary">{t('serviceSettings.loading')}</Text>
        </Flex>
      </PageTransition>
    );
  }

  if (!service) {
    return (
      <PageTransition>
        <Flex vertical gap="middle" style={{ paddingTop: token.padding }}>
          <Card>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Flex vertical align="center" gap="middle" style={{ marginTop: token.marginMD }}>
                  <Text type="secondary" style={{ fontSize: token.fontSizeLG }}>
                    {t('serviceSettings.notFound')}
                  </Text>
                  <Button
                    type="primary"
                    icon={<ArrowLeftOutlined />}
                    onClick={() => navigate({ to: '/manage/company-services' })}
                  >
                    {t('serviceSettings.backToServices')}
                  </Button>
                </Flex>
              }
            />
          </Card>
        </Flex>
      </PageTransition>
    );
  }

  const confirmationTag =
    service.confirmationMode === 'NEEDS_APPROVAL' ? (
      <Tag color="warning">{t('services.table.confirmationNeedsApproval')}</Tag>
    ) : (
      <Tag color="success">{t('services.table.confirmationDirect')}</Tag>
    );
  const isCallback = service.fulfillmentMode === 'CALLBACK';

  return (
    <PageTransition>
      <Flex vertical gap={0}>
        <DetailPageHeader
          icon={isCallback ? <PhoneOutlined /> : <ScheduleOutlined />}
          title={service.name}
          meta={
            <>
              {isCallback ? (
                <Tag icon={<PhoneOutlined />} color="purple">
                  {t('services.table.callbackRequest')}
                </Tag>
              ) : (
                <>
                  <Tag>
                    {t('services.table.durationMinutes', {
                      count: service.durationMinutes
                    })}
                  </Tag>
                  {confirmationTag}
                  {service.requiredVerification ? (
                    <Tag color="processing">
                      {t('services.table.verificationRequired')}
                    </Tag>
                  ) : null}
                </>
              )}
              <Tag color={service.isActive ? 'success' : 'default'}>
                {service.isActive
                  ? t('services.filters.active')
                  : t('services.filters.inactive')}
              </Tag>
              <Text type="secondary">{service.timezone}</Text>
            </>
          }
          actions={
            <Button
              type="default"
              icon={<TeamOutlined />}
              onClick={() => setServiceRolesDialogOpen(true)}
            >
              {t('serviceRoles.manageButton')}
            </Button>
          }
          backTo="/manage/company-services"
          backLabel={t('serviceSettings.backToServices')}
        />

        <SettingsTabsCard
          activeKey={tab}
          onChange={(nextTab) =>
            (navigate as any)({
              to: '/manage/company-services/$serviceId/manage',
              params: { serviceId },
              search: { tab: nextTab as ManageTab }
            })
          }
          items={[
            {
              key: 'general',
              icon: <SettingOutlined />,
              label: t('serviceSettings.tabs.general'),
              children: <ServiceGeneralSettings serviceId={serviceId} service={service} />
            },
            {
              key: 'availability',
              icon: <ClockCircleOutlined />,
              label: t('serviceSettings.tabs.availability'),
              children: <ServiceAvailabilitySettings serviceId={serviceId} service={service} />
            },
            {
              key: 'resources',
              icon: <ClusterOutlined />,
              label: t('serviceSettings.tabs.resources'),
              children: (
                <ServiceResourcesSettings
                  serviceId={serviceId}
                  service={service}
                  onManageRoles={() => setServiceRolesDialogOpen(true)}
                />
              )
            }
          ]}
        />
      </Flex>

      <ServiceRolesDialog
        open={serviceRolesDialogOpen}
        onClose={() => setServiceRolesDialogOpen(false)}
      />
    </PageTransition>
  );
}
